<template>
  <div class="otb-filters-modal" :class="[{ 'otb-filters-modal__request': isInRequestPage }]">
    <wb-modal :mode="filtersModalMode" close-on-escape-key scroll-lock @wbclose="toggleFiltersModal()">
      <wb-modal-header class="schedule__modal-header"></wb-modal-header>
      <wb-modal-content>
        <div class="otb-filters-menu__section-header__image" v-if="isSmallScreen">
          <img :src="vehicleImageUrl" alt="Vehicle Image" />
          <span class="otb-filters-menu__section-header__image-title wb-type-heading-s">{{ vehicleName }}</span>
        </div>
        <div class="otb-filters-menu__section-header">
          <h2 class="wb-type-copy-strong otb-filters-menu__section-title">
            <wb-icon
              class="otb-filters-menu__vehicle-options-icon"
              aria-hidden="true"
              :name="filtersMenuHeaderIcon"
            ></wb-icon>
            <span class="otb-filters-menu__section-title-label">{{ $t('shared.vehiclePreferences.button') }}</span>
          </h2>
        </div>
        <div class="otb-filters-menu__section-body">
          <template v-if="isMobile || isRequestOnTablet">
            <filter-accordion
              :icon="filterIconName('bds/fuel/24')"
              :label="fuelTypeFilterMobileLabel"
              :options="availableFuelOptions"
              :class="{ 'disabled-click': availableFuelOptions.length === 1 }"
              :selectedState="isFuelTypeSelected"
              :selectedValue="selectedFiltersTags.fuelType"
              :title="$t('schedule.filtersMenu.mobile.accordion.fuelType.title')"
              @change="selectOption"
              @close="dismissFiltersMenu"
            />
            <filter-accordion
              :icon="filterIconName('gear-xs')"
              :label="transmissionFilterMobileLabel"
              :options="availableTransmissionOptions"
              :class="{ 'disabled-click': availableTransmissionOptions.length === 1 }"
              :selectedState="isTransmissionSelected"
              :selectedValue="selectedFiltersTags.transmission"
              :title="$t('schedule.filtersMenu.mobile.accordion.transmission.title')"
              @change="selectOption"
              @close="dismissFiltersMenu"
            />
            <filter-accordion
              :icon="filterIconName('bds/engine/24')"
              :selectedState="!!selectedMotorisation"
              :label="motorisationFilterMobileLabel"
              @close="dismissFiltersMenu"
            >
              <div class="otb-filter-accordion__title">
                <wb-icon v-if="!isVansContext" class="otb-filter-accordion__title-icon" name="bds/engine/24"></wb-icon>
                <span class="wb-type-copy-strong">{{ $t('schedule.filtersMenu.mobile.accordion.engine.title') }}</span>
              </div>
              <wb-card
                v-for="(motorisation, index) in motorisationFilters"
                class="otb-filter-accordion__card"
                :class="checkedMotorisationClass(motorisation.value)"
                :key="motorisation.value"
              >
                <label
                  class="wb-type-button-secondary otb-filter-accordion__option-label"
                  v-html="motorisation.text"
                  :for="`motorisation-${index}`"
                ></label>
                <div>
                  <wb-radio-control>
                    <label class="otb-filter-accordion__input">
                      <input
                        tabindex="-1"
                        type="radio"
                        name="motorisation"
                        :checked="isMotorisationChecked(motorisation.value)"
                        :id="`motorisation-${index}`"
                        :value="motorisation.value"
                        @change="motorisationChange($event)"
                      />
                    </label>
                  </wb-radio-control>
                </div>
              </wb-card>
            </filter-accordion>
            <div v-if="isVansContext">
              <filter-accordion
                :label="driveTypeFilterMobileLabel"
                :options="availableDriveTypeOptions"
                :selectedState="isDriveTypeSelected"
                :selectedValue="selectedFiltersTags.driveType"
                :title="$t('schedule.filtersMenu.mobile.accordion.driveType.title')"
                @change="selectOption"
                @close="dismissFiltersMenu"
              />
              <filter-accordion
                :label="wheelBaseFilterMobileLabel"
                :options="availableWheelbaseOptions"
                :selectedState="isWheelbaseSelected"
                :selectedValue="selectedFiltersTags.wheelBase"
                :title="$t('schedule.filtersMenu.mobile.accordion.wheelBase.title')"
                @change="selectOption"
                @close="dismissFiltersMenu"
              />
              <filter-accordion
                :label="weightFilterMobileLabel"
                :options="availableWeightOptions"
                :selectedState="isWeightSelected"
                :selectedValue="selectedFiltersTags.weight"
                :title="$t('schedule.filtersMenu.mobile.accordion.weight.title')"
                :notification="weightNotificationInfo"
                @change="selectOption"
                @close="dismissFiltersMenu"
              />
            </div>
          </template>
          <template v-else>
            <interactive-tag-group
              class="otb-filters-menu__group"
              :icon="filterGroupIcon('bds/fuel/24')"
              :label="$t('shared.fuel.label')"
              :options="availableFuelOptions"
              :selectedValue="selectedFiltersTags.fuelType"
              @change="selectOption"
            />
            <interactive-tag-group
              class="otb-filters-menu__group"
              :icon="filterGroupIcon('gear-xs')"
              :label="$t('shared.transmission.label')"
              :options="availableTransmissionOptions"
              :selectedValue="selectedFiltersTags.transmission"
              @change="selectOption"
            >
            </interactive-tag-group>
            <fieldset>
              <h3 class="otb-filters-menu__group-title wb-type-label">
                <wb-icon class="otb-interactive-tag-group__icon" name="bds/engine/24" v-if="!isVansContext"></wb-icon
                ><span class="otb-filters-menu__group-text">{{ $t('shared.engine.label') }}</span>
              </h3>

              <wb-select-control>
                <wb-select>
                  <label class="otb-filters-menu__dropdown-label">{{ $t('shared.engine.label') }}</label>
                  <select
                    ref="motorisationSelection"
                    class="otb-filters-menu__dropdown"
                    :value="selectedMotorisationInputValue"
                    @change="motorisationChange($event)"
                  >
                    <option
                      v-for="(motorisation, index) in motorisationFilters"
                      :key="`motorisation-${index}`"
                      :value="motorisation.value"
                    >
                      {{ motorisation.text }}
                    </option>
                  </select>
                </wb-select>
              </wb-select-control>
            </fieldset>
            <div class="otb-filters-menu__vans__options" v-if="isVansContext">
              <interactive-tag-group
                class="otb-filters-menu__group"
                :label="$t('shared.driveType.label')"
                :options="availableDriveTypeOptions"
                :selectedValue="selectedFiltersTags.driveType"
                @change="selectOption"
              />
              <interactive-tag-group
                class="otb-filters-menu__group"
                :label="$t('shared.wheelBase.label')"
                :options="availableWheelBaseOptions"
                :selectedValue="selectedFiltersTags.wheelBase"
                @change="selectOption"
              />
              <interactive-tag-group
                class="otb-filters-menu__group"
                :label="$t('shared.weight.label')"
                :options="availableWeightOptions"
                :selectedValue="selectedFiltersTags.weight"
                @change="selectOption"
              />
            </div>
            <div v-if="showWeightNotification" class="otb-filters-menu__weight__notification">
              <wb-icon name="bds/info-circle/24"></wb-icon>
              <p class="otb-filters-menu__weight__notification__text wb-type-copy-secondary">
                {{ $t('schedule.booking.vehicleTile.weight.tooltip') }}
              </p>
            </div>
          </template>
          <summary-card
            v-if="(selectedMotorisation || confirmedMotorisation) && isSmallScreen"
            type="details"
            :summaryInformation="vehicleEmissionsInformation"
            :theme="'white'"
            class="otb-filters-modal__emissions"
          />
          <footnotes
            v-if="showFootNotes"
            class="request__footnotes otb-filters-modal__footnotes"
            :body="$t('shared.footnotes.text')"
            :title="$t('shared.footnotes.title')"
          />
        </div>
      </wb-modal-content>
      <wb-modal-footer class="otb-filters-modal-footer">
        <div v-if="!bookingActive" class="otb-filters-modal-footer__container">
          <button
            type="button"
            @click="resetAllVehicleFiltersInRequest"
            class="wb-button wb-button--medium otb-filters-modal__reset-btn"
            :class="resetButtonClass"
          >
            {{ $t('schedule.filtersMenu.clear') }}
          </button>
          <button
            type="button"
            @click="dismissFiltersMenu"
            class="wb-button wb-button--primary wb-button--medium otb-filters-menu__apply-btn"
          >
            <div v-if="!(isMobile || isRequestOnTablet)">
              <wb-icon v-if="isMobile" class="wb-button__icon" aria-hidden="true" name="bds/filter/24"></wb-icon>
              {{ $t('schedule.booking.timePicker.confirm') }}
            </div>

            <span v-else>{{ $t('schedule.booking.timePicker.confirm') }}</span>
          </button>
        </div>
      </wb-modal-footer>
    </wb-modal>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue';
import { i18n } from '@dh-io-otb/shared/src/services/translation.service';
import { useFilters } from '../../composables/filters.composable';
import { useRoutingApplication } from '../../composables/routing-application.composable';
import { CONTEXT_OPTIONS, ROUTER, WEIGHT_CATEGORY } from '../../models/constants';
import { useStore } from '@dh-io-otb/shared/src/composables/store.composable';
import { useMediaQuery } from '@dh-io-otb/shared/src/composables/media-query.composable';
import { useUtils } from '@dh-io-otb/shared/src/composables/utils.composable';
import CalendarService from '@dh-io-otb/shared/src/services/calendar.service';
import { SlideInformation } from '@dh-io-otb/shared/src/models/entities/text-carousel.model';
import { DateRangeMode } from '@dh-io-otb/shared/src/models/entities/calendar.model';
import { FilterAccordion, InteractiveTagGroup, SummaryCard, Footnotes } from '@dh-io-otb/shared/src/components/index';
import {
  FiltersMenuDealersListClasses,
  FilterGroupType,
  FiltersDefaultValue,
  FilterOption,
} from '../../models/entities/filters.model';
import { TRACKING_EVENT_ACTION, TRACKING_EVENT_NAME } from '@dh-io-otb/shared/src/models/constants';
import { useSummary } from '../../composables/summary.composable';

export default defineComponent({
  name: 'otb-filters-modal',
  components: {
    FilterAccordion,
    InteractiveTagGroup,
    SummaryCard,
    Footnotes,
  },
  props: {
    selectedDates: Object as () => DateRangeMode,
  },
  setup(_, { emit }) {
    const { summaryVehicleInformation } = useSummary();

    const { path } = useRoutingApplication();
    const {
      timePeriodFilters,
      fuelTypeFilters,
      transmissionFilters,
      motorisationFilters,
      allVehicleFilters,
      resetFilters,
      resetAllFiltersAndAvailableMotorisations,
      numberOfFilteredBookingResults,
      motorisationChange,
      dealerChange,
      selectOption,
      dealersFilters,
      enableClearVehiclePreferencesBtn,
      enableClearTimePeriodPreferencesBtn,
      enableClearDealersPreferencesBtn,
      trackClearFilterEvents,
      initializeFilters,
      setSingleOptionFiltersAsSelected,
      resetFiltersIfNotConfirmed,
      resetAllVehicleFiltersInRequest,
    } = useFilters();
    const { commit, getter, state } = useStore();
    const { isMobile, isMq3, isSmallScreen } = useMediaQuery();
    const { calendarType } = useUtils();
    const activeCalendarSlide = ref(0);
    const dealerCalendar = ref({ availableDates: [''], closedDates: [''] });
    const fuelTypeSelectedValue = ref('');
    const transmissionSelectedValue = ref('');
    const timePeriodSelectedValue = ref('');
    const driveTypeSelectedValue = ref('');
    const wheelBaseSelectedValue = ref('');
    const weightSelectedValue = ref('');
    const motorisationSelection = ref();
    const textSearch = ref('');
    let filteredDealers = ref();
    // lifecycle
    onMounted((): void => {
      initializeFilters();
      filteredDealers.value = state.otbcFlow.availableDealersOptions;
      dealerCalendar.value = CalendarService.buildGenericCalendar(getter.initialUnavailableDays);
    });

    const toggleFiltersModal = (): void => {
      commit('showFiltersModal', false);
      resetFiltersIfNotConfirmed();
    };
    const bookingActive = computed((): any => {
      return state.otbcFlow.bookingActive;
    });
    const localeCode = computed((): string => getter.localeCode);
    const changeDate = (day: Date): void => {
      commit('selectedDealerIds', []);
      emit('changeSelectedDate', day);
      commit('activeCalendarSlide', activeCalendarSlide.value);
    };
    const changeSheet = (page: SlideInformation): void => {
      activeCalendarSlide.value = page.index;
    };
    const filtersApplied = computed((): any => {
      const preferencesObject = {
        fuelType: state.otbcFlow.filtersApplied.fuelType,
        transmission: state.otbcFlow.filtersApplied.transmission,
        timePeriod: state.otbcFlow.filtersApplied.timePeriod,
        driveType: state.otbcFlow.filtersApplied.driveType,
        wheelBase: state.otbcFlow.filtersApplied.wheelBase,
        weight: state.otbcFlow.filtersApplied.weight,
      };
      return preferencesObject;
    });
    const filterDealerList = (availableDealers: FilterOption[], filterText: string) => {
      const filteredDealerObjects = availableDealers.filter((object: FilterOption) =>
        object.text ? (object.text as string).toLowerCase().includes(filterText.toLowerCase()) : true,
      );
      filteredDealers.value = filteredDealerObjects;
      return filteredDealerObjects;
    };
    const locationInputRoute = computed((): boolean => {
      return path.value === ROUTER.SCHEDULE;
    });

    const isInRequestPage = computed(() => {
      return path?.value === ROUTER.REQUEST;
    });
    const isRequestOnTablet = computed((): boolean => {
      return isInRequestPage.value && isMq3.value;
    });
    const filtersModalMode = computed((): string => {
      const isHandheldDevice = isMobile.value || isRequestOnTablet.value;
      return isHandheldDevice ? 'edit-mobile-fullscreen' : 'edit';
    });

    const showFootNotes = computed((): boolean => {
      return (
        state.configuration.footNotes?.visible &&
        (state.otbcFlow.confirmedMotorisation || state.otbcFlow.selectedMotorisation) &&
        isSmallScreen.value
      );
    });

    const getTextSearchResults = (): void => {
      if (textSearch.value && textSearch.value.length >= 3) {
        filterDealerList(availableDealersOptions.value, textSearch.value);
      }
      if (!textSearch.value) {
        filteredDealers.value = state.otbcFlow.availableDealersOptions;
      }
      commit('dealerTextFilterSearch', textSearch.value);
    };
    const clearDealerTextBox = (): void => {
      textSearch.value = '';
      getTextSearchResults();
    };
    const filterGroupIcon = (icon: string): string => {
      return isVansContext.value ? '' : icon;
    };
    const weightNotificationInfo = computed((): any => {
      return {
        icon: 'bds/info-circle/16',
        text: i18n.t('schedule.booking.vehicleTile.weight.tooltip'),
        value: 'HEAVY_WEIGHT',
      };
    });
    const isVansContext = computed((): boolean => {
      return state.configuration.context === CONTEXT_OPTIONS.VANS;
    });
    const filtersMenuHeaderIcon = computed((): string => {
      return isVansContext.value ? 'bds/van-frontal/24' : 'bds/car/24';
    });
    const currentlySelectedDealers = computed((): string[] => {
      return state.otbcFlow.selectedDealerIds;
    });
    const isDealersListLarge = computed((): boolean => {
      return availableDealersOptions.value.length >= 5;
    });
    const filteredBookingsSearchResults = computed((): boolean => {
      return state.otbcFlow.filteredBookingsSearchResults.length;
    });
    const availableTransmissionOptions = computed((): any[] => {
      return state.otbcFlow.availableTransmissionOptions;
    });
    const availableDealersOptions = computed((): any[] => {
      return state.otbcFlow.availableDealersOptions;
    });
    const availableFuelOptions = computed((): any[] => {
      return state.otbcFlow.availableFuelOptions;
    });
    const availableTimePeriodOptions = computed((): any[] => {
      return state.otbcFlow.availableTimePeriodOptions;
    });
    const availableDriveTypeOptions = computed((): any[] => {
      return state.otbcFlow.availableDriveTypeOptions;
    });
    const availableWheelBaseOptions = computed((): any[] => {
      return state.otbcFlow.availableWheelBaseOptions;
    });
    const availableWeightOptions = computed((): any[] => {
      return state.otbcFlow.availableWeightOptions;
    });
    const selectedMotorisation = computed((): any => {
      return state.otbcFlow.selectedMotorisation;
    });
    const selectedMotorisationInputValue = computed((): any => {
      return state.otbcFlow.selectedMotorisation && state.otbcFlow.selectedMotorisation.id
        ? state.otbcFlow.selectedMotorisation.id
        : FiltersDefaultValue.ALLMOTORISATION;
    });
    const bookingSearchFinished = computed((): boolean => {
      return state.otbcFlow.bookingSearchFinished;
    });
    const hasFixedDealer = computed((): boolean => {
      return state.otbcFlow.fixedDealer;
    });
    const filterIconName = (iconName: string): string => {
      return !isVansContext.value ? iconName : '';
    };
    const dealersListClasses = computed((): FiltersMenuDealersListClasses => {
      return {
        'otb-filters-menu__dealer--large': isDealersListLarge.value,
      };
    });
    const clearMotorisationDropdown = (): void => {
      const selectElement = motorisationSelection.value;
      const optionElement = selectElement?.querySelector('option[value="ALLMOTORISATION"]');
      if (optionElement) {
        optionElement.selected = true;
      }
    };
    const dismissFiltersMenu = (): void => {
      emit('dismiss');
      commit('confirmedFuelType', state.otbcFlow.selectedFuelType ? selectedFiltersTags.value.fuelType : '');
      commit(
        'confirmedTransmission',
        state.otbcFlow.selectedTransmission ? selectedFiltersTags.value.transmission : '',
      );
      commit('confirmedDriveType', state.otbcFlow.selectedDriveType ? selectedFiltersTags.value.driveType : '');
      commit('confirmedWheelBase', state.otbcFlow.selectedWheelBase ? selectedFiltersTags.value.wheelBase : '');
      commit('confirmedWeight', state.otbcFlow.selectedWeight ? selectedFiltersTags.value.weight : '');
      commit('confirmedMotorisation', state.otbcFlow.selectedMotorisation || '');
      setSingleOptionFiltersAsSelected();
    };
    const selectedFiltersTags = computed((): any => {
      return {
        fuelType: fuelTypeInputSelected(),
        transmission: transmissionInputSelected(),
        timePeriod: timePeriodInputSelected(),
        driveType: driveTypeInputSelected(),
        wheelBase: wheelBaseInputSelected(),
        weight: weightInputSelected(),
      };
    });
    const driveTypeInputSelected = (): string => {
      if (state.otbcFlow.availableDriveTypeOptions && state.otbcFlow.availableDriveTypeOptions.length === 1) {
        driveTypeSelectedValue.value = state.otbcFlow.availableDriveTypeOptions[0].value;
        return driveTypeSelectedValue.value;
      }
      driveTypeSelectedValue.value = !filtersApplied.value.driveType
        ? FiltersDefaultValue.ALLDRIVETYPE
        : filtersApplied.value.driveType;
      return driveTypeSelectedValue.value;
    };
    const wheelBaseInputSelected = (): string => {
      if (state.otbcFlow.availableWheelBaseOptions && state.otbcFlow.availableWheelBaseOptions.length === 1) {
        wheelBaseSelectedValue.value = state.otbcFlow.availableWheelBaseOptions[0].value;
        return wheelBaseSelectedValue.value;
      }
      wheelBaseSelectedValue.value = !filtersApplied.value.wheelBase
        ? FiltersDefaultValue.ALLWHEELBASE
        : filtersApplied.value.wheelBase;
      return wheelBaseSelectedValue.value;
    };
    const weightInputSelected = (): string => {
      if (state.otbcFlow.availableWeightOptions && state.otbcFlow.availableWeightOptions.length === 1) {
        weightSelectedValue.value = state.otbcFlow.availableWeightOptions[0].value;
        return weightSelectedValue.value;
      }
      weightSelectedValue.value = !filtersApplied.value.weight
        ? FiltersDefaultValue.ALLWEIGHT
        : filtersApplied.value.weight;
      return weightSelectedValue.value;
    };
    const fuelTypeInputSelected = (): string => {
      if (state.otbcFlow.availableFuelOptions && state.otbcFlow.availableFuelOptions.length === 1) {
        fuelTypeSelectedValue.value = state.otbcFlow.availableFuelOptions[0].value;
        return fuelTypeSelectedValue.value;
      }
      fuelTypeSelectedValue.value = !filtersApplied.value.fuelType
        ? FiltersDefaultValue.ALLFUELTYPE
        : filtersApplied.value.fuelType;
      return fuelTypeSelectedValue.value;
    };
    const transmissionInputSelected = (): string => {
      if (state.otbcFlow.availableTransmissionOptions && state.otbcFlow.availableTransmissionOptions.length === 1) {
        transmissionSelectedValue.value = state.otbcFlow.availableTransmissionOptions[0].value;
        return transmissionSelectedValue.value;
      }
      transmissionSelectedValue.value = !filtersApplied.value.transmission
        ? FiltersDefaultValue.ALLTRANSMISSION
        : filtersApplied.value.transmission;
      return transmissionSelectedValue.value;
    };

    const timePeriodInputSelected = (): string => {
      timePeriodSelectedValue.value = !filtersApplied.value.timePeriod
        ? FiltersDefaultValue.ALLDAY
        : filtersApplied.value.timePeriod;
      return timePeriodSelectedValue.value;
    };

    const isMotorisationChecked = (motorisation: string): boolean => {
      if (
        (motorisation === FiltersDefaultValue.ALLMOTORISATION && !state.otbcFlow.selectedMotorisation) ||
        (state.otbcFlow.selectedMotorisation && state.otbcFlow.selectedMotorisation.id === motorisation)
      ) {
        return true;
      }
      return false;
    };

    const fuelTypeFilterMobileLabel = computed(() => {
      const fuelType = availableFuelOptions.value.find((option) => option.value === selectedFiltersTags.value.fuelType);
      return fuelType && fuelType.value !== FiltersDefaultValue.ALLFUELTYPE
        ? fuelType.text
        : i18n.t('schedule.filtersMenu.mobile.accordion.fuelType.title');
    });

    const transmissionFilterMobileLabel = computed(() => {
      const transmission = availableTransmissionOptions.value.find(
        (option) => option.value === selectedFiltersTags.value.transmission,
      );
      return transmission && transmission.value !== FiltersDefaultValue.ALLTRANSMISSION
        ? transmission.text
        : i18n.t('schedule.filtersMenu.mobile.accordion.transmission.title');
    });

    const driveTypeFilterMobileLabel = computed(() => {
      const driveType = availableDriveTypeOptions.value.find(
        (option) => option.value === selectedFiltersTags.value.driveType,
      );
      return driveType && driveType.value !== FiltersDefaultValue.ALLDRIVETYPE
        ? driveType.text
        : i18n.t('schedule.filtersMenu.mobile.accordion.driveType.title');
    });

    const wheelBaseFilterMobileLabel = computed(() => {
      const wheelBase = availableWheelBaseOptions.value.find(
        (option) => option.value === selectedFiltersTags.value.wheelBase,
      );
      return wheelBase && wheelBase.value !== FiltersDefaultValue.ALLWHEELBASE
        ? wheelBase.text
        : i18n.t('schedule.filtersMenu.mobile.accordion.wheelBase.title');
    });

    const weightFilterMobileLabel = computed(() => {
      const weight = availableWeightOptions.value.find((option) => option.value === selectedFiltersTags.value.weight);
      return weight && weight.value !== FiltersDefaultValue.ALLWEIGHT
        ? weight.text
        : i18n.t('schedule.filtersMenu.mobile.accordion.weight.title');
    });

    const motorisationFilterMobileLabel = computed(() => {
      const motorisation = motorisationFilters.value.find((option) => {
        if (state.otbcFlow.selectedMotorisation && state.otbcFlow.selectedMotorisation.id) {
          return option.value === state.otbcFlow.selectedMotorisation.id;
        }
        return false;
      });
      return motorisation ? motorisation.text : i18n.t('schedule.filtersMenu.mobile.accordion.motorisation.title');
    });

    const timeOptionsFilterMobileLabel = computed(() => {
      const period = availableTimePeriodOptions.value.find(
        (option) => option.value === selectedFiltersTags.value.timePeriod,
      );
      return period && period.value !== FiltersDefaultValue.ALLDAY
        ? period.text
        : i18n.t('schedule.filtersMenu.mobile.accordion.dayPeriod.title');
    });

    const timeOptionsFilterTitleLabel = computed(() => {
      return isMobile.value
        ? i18n.t('schedule.filtersMenu.mobile.dayPeriod.title')
        : i18n.t('schedule.filtersMenu.dayPeriodOptions');
    });

    const dealersFilterMobileLabel = computed(() => {
      return currentlySelectedDealers.value.length > 0
        ? currentlySelectedDealers.value.join(', ')
        : i18n.t('schedule.filtersMenu.mobile.retailers.title');
    });

    const showDesktopSearchBar = computed(() => {
      return isDealersListLarge.value && !isMobile.value;
    });

    const showMobileSearchBar = computed(() => {
      return isDealersListLarge.value && bookingSearchFinished.value;
    });

    const checkedMotorisationClass = (motorisation: string): string =>
      isMotorisationChecked(motorisation) ? 'otb-filter-accordion__toggle-label--checked' : '';
    const checkedDealerClass = (dealer: string): string =>
      currentlySelectedDealers.value.includes(dealer) ? 'otb-filter-accordion__toggle-label--checked' : '';
    const isFuelTypeSelected = computed((): boolean => {
      return (
        (filtersApplied.value.fuelType !== FiltersDefaultValue.ALLFUELTYPE && !!filtersApplied.value.fuelType) ||
        availableFuelOptions.value.length === 1
      );
    });

    const isTransmissionSelected = computed((): boolean => {
      return (
        (filtersApplied.value.transmission !== FiltersDefaultValue.ALLTRANSMISSION &&
          !!filtersApplied.value.transmission) ||
        availableTransmissionOptions.value.length === 1
      );
    });

    const isTimePeriodSelected = computed((): boolean => {
      return (
        (filtersApplied.value.timePeriod !== FiltersDefaultValue.ALLDAY && !!filtersApplied.value.timePeriod) ||
        availableTimePeriodOptions.value.length === 1
      );
    });

    const isDriveTypeSelected = computed((): boolean => {
      return (
        (filtersApplied.value.driveType !== FiltersDefaultValue.ALLDRIVETYPE && !!filtersApplied.value.driveType) ||
        availableDriveTypeOptions.value.length === 1
      );
    });

    const isWheelBaseSelected = computed((): boolean => {
      return (
        (filtersApplied.value.wheelBase !== FiltersDefaultValue.ALLWHEELBASE && !!filtersApplied.value.wheelBase) ||
        availableWheelBaseOptions.value.length === 1
      );
    });

    const isWeightSelected = computed((): boolean => {
      return (
        (filtersApplied.value.weight !== FiltersDefaultValue.ALLWEIGHT && !!filtersApplied.value.weight) ||
        availableWeightOptions.value.length === 1
      );
    });

    const showWeightNotification = computed(() => {
      return (
        (state.otbcFlow.selectedWeight === WEIGHT_CATEGORY.HEAVY_WEIGHT ||
          state.otbcFlow.selectedMotorisation?.weightCategory === WEIGHT_CATEGORY.HEAVY_WEIGHT) &&
        !isMobile.value
      );
    });
    const dealerFilterResetMethods = (): void => {
      resetFilters(FilterGroupType.DEALERS);
      clearDealerTextBox();
      trackClearFilterEvents(TRACKING_EVENT_ACTION.RETAILER, TRACKING_EVENT_NAME.APPLY_FILTER);
    };
    const vehicleFilterResetMethods = (): void => {
      resetFilters(FilterGroupType.VEHICLE);
      clearMotorisationDropdown();
      trackClearFilterEvents(TRACKING_EVENT_ACTION.VEHICLE, TRACKING_EVENT_NAME.APPLY_FILTER);
    };

    const periodFilterResetMethods = (): void => {
      resetFilters(FilterGroupType.TIME_PERIOD);
      trackClearFilterEvents(TRACKING_EVENT_ACTION.PERIOD, TRACKING_EVENT_NAME.APPLY_FILTER);
    };

    const vehicleImageUrl = computed(() => {
      return state.otbcFlow.vehiclePreferences?.image.url;
    });

    const vehicleName = computed(() => {
      return state.otbcFlow.model?.name;
    });

    const vehicleEmissionsInformation = computed(() => {
      return state.otbcFlow.vehiclePreferences?.secondary;
    });

    const resetButtonClass = computed(() => {
      const isHandheldDevice = isMobile.value || isRequestOnTablet.value;
      return isHandheldDevice ? 'wb-button--tertiary' : 'wb-button--secondary';
    });

    return {
      // METHODS
      changeDate,
      changeSheet,
      checkedDealerClass,
      checkedMotorisationClass,
      dismissFiltersMenu,
      fuelTypeInputSelected,
      isMotorisationChecked,
      transmissionInputSelected,
      timePeriodInputSelected,
      getTextSearchResults,
      clearDealerTextBox,
      dealerFilterResetMethods,
      vehicleFilterResetMethods,
      periodFilterResetMethods,
      filterGroupIcon,
      filterIconName,
      // FROM FILTERS COMPOSABLE
      timePeriodFilters,
      fuelTypeFilters,
      transmissionFilters,
      motorisationFilters,
      dealersFilters,
      allVehicleFilters,
      resetFilters,
      resetAllFiltersAndAvailableMotorisations,
      numberOfFilteredBookingResults,
      motorisationChange,
      dealerChange,
      selectOption,
      enableClearVehiclePreferencesBtn,
      enableClearTimePeriodPreferencesBtn,
      enableClearDealersPreferencesBtn,
      trackClearFilterEvents,
      resetFiltersIfNotConfirmed,
      resetAllVehicleFiltersInRequest,

      // CONSTANTS & ENUMS
      FilterGroupType,
      FiltersDefaultValue,
      // COMPUTEDS
      availableTransmissionOptions,
      availableFuelOptions,
      availableDealersOptions,
      availableTimePeriodOptions,
      availableDriveTypeOptions,
      availableWheelBaseOptions,
      availableWeightOptions,
      bookingSearchFinished,
      isInRequestPage,
      isRequestOnTablet,
      filtersModalMode,
      calendarType,
      dealersFilterMobileLabel,
      filteredBookingsSearchResults,
      fuelTypeFilterMobileLabel,
      isDealersListLarge,
      isFuelTypeSelected,
      isDriveTypeSelected,
      isWheelBaseSelected,
      isWeightSelected,
      isMobile,
      isSmallScreen,
      isTimePeriodSelected,
      isTransmissionSelected,
      localeCode,
      locationInputRoute,
      motorisationFilterMobileLabel,
      showDesktopSearchBar,
      showMobileSearchBar,
      timeOptionsFilterMobileLabel,
      timeOptionsFilterTitleLabel,
      transmissionFilterMobileLabel,
      driveTypeFilterMobileLabel,
      wheelBaseFilterMobileLabel,
      weightFilterMobileLabel,
      dealersListClasses,
      currentlySelectedDealers,
      filtersApplied,
      selectedFiltersTags,
      hasFixedDealer,
      selectedMotorisationInputValue,
      filtersMenuHeaderIcon,
      isVansContext,
      weightNotificationInfo,
      showWeightNotification,
      bookingActive,
      vehicleImageUrl,
      vehicleName,
      vehicleEmissionsInformation,
      showFootNotes,
      resetButtonClass,

      // REFS
      activeCalendarSlide,
      dealerCalendar,
      textSearch,
      filteredDealers,
      clearMotorisationDropdown,
      motorisationSelection,
      selectedMotorisation,
      toggleFiltersModal,
      summaryVehicleInformation,
    };
  },
});
</script>

<style lang="scss" src="./filters-modal.scss" scoped></style>
